import { useCallback, useEffect, useRef, useState, useReducer } from "react"
import * as React from "react"
import { useLanguageState } from "../globalStates/LanguageState"
import { defaultLogger as logger } from "../globalStates/AppState"
import ChatMessageElement from "./ChatMessageElement"
import moment from "moment"
import {
    loadChatMessages,
    ChatMessage,
    getUserById,
    UserResponse,
    setLastReadConversation,
    getLastReadUserConversation,
    buildGraphqlArgs
} from "../backendServices/GraphQLServices" // eslint-disable-line react-hooks/exhaustive-deps
import { API } from "aws-amplify"
import Observable from "zen-observable-ts"
import { onMessageCreatedLight } from "../graphql/ownSubscriptions"
import { useNotificationContext } from "../conference/context/NotificationContext"
import { ConversationType, OnMessageCreatedLightSubscription } from "../API"
import { isToday, isYesterday } from "../utils/DateUtils"
import { ConversationParticipant, DisplayStyle } from "./ChatPage"
import ContactModal from "../ui/modals/ContactModal"
import { backOff } from "exponential-backoff"
import { SideBarViewMode } from "../conference/components/Roster"
import InView from "react-intersection-observer"
import {
    ChatHeaderItem,
    ConversationContainer,
    HeaderElement,
    NextPageLoader,
    ScrollingContainer
} from "./ChatPageMessageList.styled"

interface NewMessageSubscription {
    value: {
        data: OnMessageCreatedLightSubscription
    }
}

interface MessageListPanelProps {
    profileId: string
    conversationId?: string
    conversationType: ConversationType
    participants: Map<string, ConversationParticipant>
    displayStyle?: DisplayStyle
    userConversationId?: string
    viewMode?: SideBarViewMode
    virtualCafe?: boolean
}

const ChatPageMessageList: React.FC<MessageListPanelProps> = (props) => {
    const MemoizedChatMessageElement = React.memo(ChatMessageElement)
    const MemoizedChatHeaderElement = React.memo(HeaderElement)
    const languageState = useLanguageState()
    const language = languageState.getLanguage()
    const conversationScrollContainerRef = useRef<HTMLDivElement>(null)
    const chatItemsRef = useRef<ChatItem[]>([])
    const notificationContext = useNotificationContext()
    const knownMessageIdsRef = useRef<Set<string>>(new Set())
    const [nextPageToken, setNextPageToken] = useState<string | undefined>()
    const [pinToBottom, setPinToBottom] = useState(true)
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [showContactModal, setShowContactModal] = useState<boolean>(false)
    const [targetId, setTargetId] = useState<string>("")
    const audio = new Audio("/branding/applicationMedia/sounds/notificationSound.mp3")
    const [isChatDataLoaded, setIsChatDataLoaded] = useState(false)
    const [chatItems, dispatchChatItems] = useReducer(() => {
        return [...chatItemsRef.current].reverse()
    }, [])

    useEffect(() => {
        if (props.conversationId) {
            notificationContext.addMutedConversationId(props.conversationId)
            if (props.userConversationId !== undefined) {
                let id = props.userConversationId
                ;(async () => {
                    const userConversation = await getLastReadUserConversation(id)
                    const lastRead = userConversation?.lastReadMessageCreatedAt
                    const mostRecentMessage = userConversation?.mostRecentMessageCreatedAt
                    const alreadyRead =
                        mostRecentMessage === null ||
                        (lastRead === null && mostRecentMessage === null) ||
                        userConversation?.lastReadMessageCreatedAt === userConversation?.mostRecentMessageCreatedAt
                    if (!alreadyRead) {
                        setLastReadConversation(props.userConversationId as string, mostRecentMessage!)
                    }
                })()
            }
        }
        return () => {
            if (props.conversationId) {
                notificationContext.removeMutedConversationId(props.conversationId)
            }
        }
    }, [props.conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (pinToBottom) scrollToBottom(true)
    }, [chatItems]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.viewMode === SideBarViewMode.CHAT) scrollToBottom(true)
    }, [props.viewMode, chatItems, scrollerRef])

    const showOpponentName =
        true /** The oponent name was not displayed for chats 1 on 1 so I set this to true. Old configuration is above  */
    const showAuthorAvatar = props.conversationType === ConversationType.PUBLIC
    const showDateSections = props.displayStyle !== DisplayStyle.PINBOARD

    const newMessageItemsCallback = useCallback(
        async (chatMessagesToAdd: ChatMessage[], index?: number) => {
            const missingOpponentIds = new Set<string>()
            chatMessagesToAdd.forEach((m) => knownMessageIdsRef.current.add(m.id))
            const newChatMessages = chatMessagesToAdd.map((msg) => {
                const isMine = msg.authorId === props.profileId
                const showAuthorName = showOpponentName && (!isMine || props.displayStyle === DisplayStyle.PINBOARD)
                const author = props.participants.get(msg.authorId)
                const authorName = showAuthorName ? author?.name ?? null : undefined
                const authorImg = showAuthorAvatar ? author?.pictureUrl ?? null : undefined
                if (authorName === null) {
                    missingOpponentIds.add(msg.authorId)
                }
                return new ChatMessageItem(msg.timestamp, msg.id, msg.content, msg.authorId, authorName, authorImg, isMine)
            })

            if (showDateSections) {
                addChatItemsWithHeaders(chatItemsRef.current, newChatMessages, index, props.virtualCafe ? audio : undefined)
            } else {
                addChatItemsWithoutHeaders(chatItemsRef.current, newChatMessages, index, props.virtualCafe ? audio : undefined)
            }

            dispatchChatItems()

            if (missingOpponentIds.size <= 0) return

            const userResponses = await Promise.all(Array.from(missingOpponentIds).map(async (opId) => getUserById(opId)))
            const opponents = userResponses
                .map((response) => (response as UserResponse)?.getUser)
                .filter(Boolean)
                .map((user) => {
                    return { id: user!.id, name: user!.name, pictureUrl: user!.pictureUrl }
                })
            opponents.forEach((opponent) => props.participants.set(opponent.id, opponent))

            newChatMessages.forEach((chatItem) => {
                if (missingOpponentIds.has(chatItem.authorId)) {
                    const author = props.participants.get(chatItem.authorId)
                    chatItem.authorName = author?.name
                    chatItem.authorPictureUrl = showAuthorAvatar ? author?.pictureUrl : undefined
                }
            })
            dispatchChatItems()
            // eslint-disable-next-line
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.participants, props.profileId, showOpponentName, showAuthorAvatar, props.displayStyle, showDateSections]
    )

    const newMessageItemsCallbackRef = useRef(newMessageItemsCallback)
    newMessageItemsCallbackRef.current = newMessageItemsCallback

    useEffect(() => {
        if (props.conversationId) {
            const onCreateMessageHandle = (
                API.graphql(
                    buildGraphqlArgs(onMessageCreatedLight, { conversationId: props.conversationId })
                ) as Observable<NewMessageSubscription>
            ).subscribe({
                next: (resp: NewMessageSubscription) => {
                    const msg = resp.value.data.onMessageCreated

                    if (msg && props.userConversationId)
                        (async () => {
                            setLastReadConversation(props.userConversationId as string, msg.createdAt)
                        })()
                    if (msg && !knownMessageIdsRef.current.has(msg.id)) {
                        scrollToBottom(false)
                        const bottomMostExistingMessageIndex = chatItemsRef.current.findIndex((c) => c instanceof ChatMessageItem)
                        // const bottomMostExistingMessageIndex = chatItems.findIndex(c => c instanceof OpponentMessageItem || (c instanceof MyMessageItem && !c.failed)) // TODO if we show unsent/failed messages we need something like this
                        newMessageItemsCallbackRef.current(
                            [
                                {
                                    id: msg.id,
                                    content: msg.content,
                                    authorId: msg.authorId,
                                    isSent: true,
                                    timestamp: new Date(msg.createdAt),
                                    conversationId: msg.conversationId
                                }
                            ],
                            bottomMostExistingMessageIndex
                        )
                        dispatchChatItems()
                    }
                    if (audio && document.visibilityState === "hidden" && props.virtualCafe) {
                        audio.play()
                    }
                },
                error: (error) => {
                    logger.error({
                        message: "ChatPageMessageList.tsx Subscription Error Message Create",
                        errorMessage: error.message,
                        errorStack: error.stack
                    })
                },
                complete: () => {
                    logger.info("Subscription Complete Message Create")
                }
            })
            return () => onCreateMessageHandle.unsubscribe()
        }
    }, [props.conversationId, props.userConversationId]) //eslint-disable-line

    const loadNextPageCallback = useCallback(async () => {
        if (!props.conversationId) return
        setIsChatDataLoaded(false)
        const scroller = scrollerRef.current
        var scrollPos = scroller?.scrollTop ?? 0
        var scrollableHeightOld = scroller ? scroller.scrollHeight - scroller.clientHeight : 0

        const result = await backOff(() => loadChatMessages(props.conversationId!, nextPageToken), {
            retry: (error: any, attemptNumber: number) => {
                logger.error({
                    message: "ChatPageMesseageList loadChatMessages attempt " + attemptNumber + " failed.",
                    errorMessage: error.message,
                    errorStack: error.stack
                })
                return true
            }
        })
        if (result) {
            setIsChatDataLoaded(true)
            const [newMessages, nextToken] = result
            setNextPageToken(nextToken)
            newMessageItemsCallbackRef.current(newMessages)

            if (scroller) {
                const scrollableHeightNew = scroller.scrollHeight - scroller.clientHeight
                const scrollableHeightDiff = scrollableHeightNew - scrollableHeightOld
                scroller.scrollTop = scrollPos + scrollableHeightDiff
            }
        } else {
            logger.warn("ChatPageMesseageList loadChatMessages failed, giving up")
        }
    }, [props.conversationId, nextPageToken, scrollerRef])

    useEffect(() => {
        // Conversation changed => clear everything and load first page
        setNextPageToken(undefined)
        knownMessageIdsRef.current.clear()
        chatItemsRef.current.length = 0
        dispatchChatItems()
        loadNextPageCallback()
    }, [props.conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

    const scrollToBottom = (animate: boolean) => {
        setTimeout(() => {
            conversationScrollContainerRef?.current?.scrollTo({
                top: conversationScrollContainerRef.current.scrollHeight,
                behavior: animate ? "smooth" : "auto"
            })
        }, 500) // The  scroll to bottom didn't work because of the delay of the messages, so I added set timeout
    }

    moment.locale(language)
    return (
        <>
            <ScrollingContainer
                ref={conversationScrollContainerRef}
                className="chatlist-scrollref"
                style={{
                    backgroundColor: props.displayStyle === DisplayStyle.DEFAULT_DARK ? "#313233" : "#fff"
                }}
            >
                <ConversationContainer>
                    {nextPageToken && isChatDataLoaded && (
                        <InView
                            onChange={(inView) => {
                                if (inView) loadNextPageCallback()
                            }}
                        >
                            <NextPageLoader size="md" />
                        </InView>
                    )}
                    {/* <MessageItemBubble className={"neutral"}>System message</MessageItemBubble> */}
                    {chatItems.map((chatItem, index) => {
                        if (chatItem instanceof ChatMessageItem) {
                            if (chatItem.authorName === null || props.displayStyle === DisplayStyle.PINBOARD) {
                                // no memoization for pending author name or pinboard style where relative time on message changes with language
                                return (
                                    <ChatMessageElement
                                        key={index}
                                        chatMessageItem={chatItem}
                                        displayStyle={props.displayStyle}
                                        setTargetId={setTargetId}
                                        setShowContactModal={setShowContactModal}
                                        isPublic={props.conversationType === ConversationType.PUBLIC}
                                    />
                                )
                            } else {
                                return (
                                    <MemoizedChatMessageElement
                                        key={index}
                                        chatMessageItem={chatItem}
                                        displayStyle={props.displayStyle}
                                        setTargetId={setTargetId}
                                        setShowContactModal={setShowContactModal}
                                        isPublic={props.conversationType === ConversationType.PUBLIC}
                                    />
                                )
                            }
                        } else {
                            return (
                                <MemoizedChatHeaderElement key={index}>
                                    <ChatHeaderItem>{calcHeaderItemTime(chatItem.timestamp)}</ChatHeaderItem>
                                </MemoizedChatHeaderElement>
                            )
                        }
                    })}
                    <InView
                        onChange={(inView) => {
                            if (!inView) {
                                setPinToBottom(false)
                            }
                        }}
                    ></InView>
                </ConversationContainer>
            </ScrollingContainer>
            {showContactModal && <ContactModal userId={targetId} close={() => setShowContactModal(false)}></ContactModal>}
        </>
    )
}

function calcHeaderItemTime(time: Date): string {
    const timeMoment = moment(time)
    if (isToday(timeMoment) || isYesterday(timeMoment)) {
        const timeString = timeMoment.calendar().split(" ")[0]
        return timeString.charAt(0).toUpperCase() + timeString.slice(1)
    }
    if (moment().diff(timeMoment, "days") < 7) {
        // show relative dates for a week
        return timeMoment.format("dddd")
    }
    return timeMoment.format("L")
}

function addChatItemsWithoutHeaders(
    existingChatItems: ChatItem[],
    newMessages: ChatItem[],
    index: number | undefined = undefined,
    audio?: HTMLAudioElement
) {
    if (index === undefined) {
        existingChatItems.push(...newMessages)
    } else {
        existingChatItems.splice(index, 0, ...newMessages)
    }
}

function addChatItemsWithHeaders(
    existingChatItems: ChatItem[],
    newMessages: ChatItem[],
    index: number | undefined = undefined,
    audio?: HTMLAudioElement
) {
    const result = existingChatItems //[...existingChatItems]
    const lastIndex = result.length - 1
    let insertIndex = index
    if (index === undefined && result.length > 0) {
        const previousMessage = result.pop()
        newMessages.unshift(previousMessage!)
    } else if (index !== undefined) {
        if (index >= 0 && index < result.length) {
            const previousMessage = result[index]
            newMessages.push(previousMessage)
            result.splice(index, 1)
        }
        if (index - 1 >= 0 && index - 1 < result.length) {
            const nextMessage = result[index - 1]
            newMessages.unshift(nextMessage)
            result.splice(index - 1, 1)
            insertIndex = index - 1
        }
    }
    if (insertIndex === undefined) {
        calcHeaderItems(newMessages, true)
        result.push(...newMessages)
    } else {
        calcHeaderItems(newMessages, insertIndex === lastIndex)
        result.splice(insertIndex, 0, ...newMessages)
    }
}

function calcHeaderItems(chatItems: ChatItem[], addEndHeader: Boolean) {
    let previousItem: ChatItem | undefined
    let i = 0
    while (i < chatItems.length) {
        const chatItem = chatItems[i]

        if (previousItem != null) {
            const isSameDay = checkSameDay(previousItem.timestamp, chatItem.timestamp)
            const previousItemIsHeader = previousItem instanceof HeaderItem
            if (previousItemIsHeader && isSameDay) {
                chatItems.splice(i - 1, 1) // chatItems.removeAt(i-1)
                i--
            }
            if (!previousItemIsHeader && !isSameDay) {
                chatItems.splice(i, 0, new HeaderItem(previousItem.timestamp))
                i++
            }
        }

        previousItem = chatItem
        i++
    }

    if (previousItem != null && addEndHeader) {
        chatItems.push(new HeaderItem(previousItem.timestamp))
    }
}

function checkSameDay(first: Date, second: Date) {
    return (
        first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate()
    )
}

export class ChatItem {
    timestamp: Date
    constructor(timestamp: Date) {
        this.timestamp = timestamp
    }
}
export class ChatMessageItem extends ChatItem {
    id: string
    content: string
    authorId: string
    authorName?: string | null
    authorPictureUrl?: string | null
    isMine: boolean
    constructor(
        timestamp: Date,
        id: string,
        content: string,
        authorId: string,
        authorName: string | null | undefined,
        authorPictureUrl: string | null | undefined,
        isMine: boolean
    ) {
        super(timestamp)
        this.id = id
        this.content = content
        this.authorId = authorId
        this.authorName = authorName
        this.authorPictureUrl = authorPictureUrl
        this.isMine = isMine
    }
}
export class HeaderItem extends ChatItem {
    // constructor(timestamp: Date) {
    //     super(timestamp)
    // }
}

export function getTypeFromUrl(
    urlString: string
): "organization" | "person" | "user" | "eventdate" | "joboffer" | "product" | "trademark" | "news" {
    let url: URL = new URL(urlString)

    let type: string = url.pathname.substring(1).split("/")[0]

    if (type === "person") {
        let userOrPerson: string = url.pathname.substring(1).split("--")[1].split("-")[0]

        if (userOrPerson === "u") {
            return "user"
        } else {
            return "person"
        }
    } else if (type === "company") {
        return "organization"
    } else if (type === "eventdate" || type === "joboffer" || type === "product" || type === "trademark" || type === "news") {
        return type
    }

    return "eventdate"
}

export function getIdFromUrl(urlString: string): string {
    let url: URL = new URL(urlString)

    let splitUrl: string[] =
        getTypeFromUrl(urlString) === "person"
            ? url.pathname.substring(1).split("--p-")
            : getTypeFromUrl(urlString) === "user"
            ? url.pathname.substring(1).split("--u-")
            : url.pathname.substring(1).split("--")

    return splitUrl[splitUrl.length - 1]
}

export function getNameFromUrl(urlString: string): string {
    let url: URL = new URL(urlString)
    let type: string = getTypeFromUrl(urlString)

    if (type === "person") {
        return url.pathname.split("person/")[1].split("--p" + getIdFromUrl(urlString))[0]
    } else if (type === "user") {
        return url.pathname.split("person/person-")[1].split("--u" + getIdFromUrl(urlString))[0]
    } else if (type === "organization") {
        return url.pathname.split("company/")[1].split("--" + getIdFromUrl(urlString))[0]
    }

    return url.pathname.split(type + "/")[1].split("--" + getIdFromUrl(urlString))[0]
}

export default ChatPageMessageList
